import { useEffect, useState } from "react";
import { Hoja } from "@/components/Hoja";
import { RemansoFigure } from "@/components/Brand";
import { CrisisNote } from "@/components/CrisisNote";
import { HabitCard } from "@/components/HabitCard";
import { HabitForm } from "@/components/HabitForm";
import { api, errorText } from "@/lib/api";
import { ymd } from "@/lib/habits";

// one retry; if it still fails, the call and error go to the server log
const loadWithRetry = (path) => api.get(path).catch(() => api.get(path)).then((r) => r.data, (err) => {
  api.post("/client-log", { where: "habitos", call: `GET ${path}`, status: err?.response?.status ?? null,
    message: String(err?.response?.data?.detail || err?.message || "") }).catch(() => {});
  throw err;
});

const EmptyHabits = () => (
  <div className="flex flex-col sm:flex-row gap-6 items-start max-w-2xl" data-testid="habits-empty">
    <RemansoFigure estado="espera" height={96} className="shrink-0" testId="habits-empty-figure" />
    <div>
      <p className="text-[16px] text-sec leading-relaxed">
        Aquí puedes cambiar un mal hábito o crear uno nuevo. Respondes unas preguntas cortas, empiezas por la versión más pequeña y cada día marcas cómo te fue.
      </p>
      <div className="mt-5 rounded-[24px] bg-superficie p-5" data-testid="habits-example">
        <p className="eyebrow text-xs">Ejemplo</p>
        <p className="font-serif text-[22px] leading-snug text-tinta mt-2">Todos los días dejo mi celular cargando en la cocina y me duermo a las 11.</p>
      </div>
    </div>
  </div>
);

export default function Habitos() {
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState({});
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [habitsOk, setHabitsOk] = useState(false);
  const [logsOk, setLogsOk] = useState(false);

  useEffect(() => {
    const habitsLoad = loadWithRetry("/habits").then((data) => { setHabits(data); setHabitsOk(true); })
      .catch(() => setError("No pudimos traer tus hábitos en este momento. Intenta de nuevo en un rato."));
    const logsLoad = loadWithRetry("/habit-logs").then((data) => {
      const map = {};
      data.forEach((x) => { (map[x.habit_id] ||= {})[x.date] = x.status; });
      setLogs(map);
      setLogsOk(true);
    }).catch(() => setError("No pudimos traer los días que marcaste. Tus hábitos siguen aquí; intenta de nuevo en un rato."));
    Promise.all([habitsLoad, logsLoad]).finally(() => setLoaded(true));
  }, []);

  const setDay = async (habitId, date, status) => {
    const prev = logs[habitId]?.[date];
    setLogs((m) => ({ ...m, [habitId]: { ...m[habitId], [date]: status } }));
    try {
      await api.post("/habit-logs", { habit_id: habitId, date, status });
    } catch (err) {
      setLogs((m) => ({ ...m, [habitId]: { ...m[habitId], [date]: prev } }));
      setError(errorText(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/habits/${id}`);
      setHabits((list) => list.filter((h) => h.id !== id));
    } catch (err) {
      setError(errorText(err));
    }
  };

  const saved = (h) => {
    setError("");
    setHabits((list) => (list.some((x) => x.id === h.id) ? list.map((x) => (x.id === h.id ? h : x)) : [...list, h]));
    setForm(null);
  };

  const today = ymd(new Date());
  const doneToday = habits.filter((h) => logs[h.id]?.[today] === "si").length;
  const date = new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });

  return (
    <Hoja testId="screen-habitos" title={form ? "Registrar un hábito" : "Hábitos"}
      subtitle={form ? null : logsOk ? `${date} · Hoy llevas ${doneToday} de ${habits.length}` : date}
      aside={!form && <button className="btn-primary shrink-0" onClick={() => setForm({})} data-testid="habit-register-button">Registrar un hábito</button>}>
      {error && <p className="calm-note mb-6" role="status" data-testid="habits-error">{error}</p>}
      {form ? (
        <HabitForm initial={form.habit} onSaved={saved} onCancel={() => setForm(null)} />
      ) : (
        <>
          {loaded && habitsOk && habits.length === 0 && <EmptyHabits />}
          <div className="grid gap-5 xl:grid-cols-2" data-testid="habit-list">
            {habits.map((h) => (
              <HabitCard key={h.id} habit={h} showCounts={logsOk} byDate={logs[h.id] || {}} onSet={(d, s) => setDay(h.id, d, s)}
                onEdit={() => setForm({ habit: h })} onDelete={() => remove(h.id)} />
            ))}
          </div>
          <p className="text-[13px] text-sec mt-10">
            Pensive no te dice qué hábitos cambiar ni cómo. Tú decides el hábito; aquí solo llevas la cuenta.
          </p>
        </>
      )}
      <div className="hidden lg:block mt-10 pt-5 border-t border-linea max-w-2xl">
        <CrisisNote testId="habits-crisis-note" />
      </div>
    </Hoja>
  );
}
